import { Forms, Tooltip } from "@types/FormTypes";
import { __ } from "@wordpress/i18n";
import { validations } from "./Validations";

/**
 * LABELS
 *
 * Centralized field labels for the Savings Certificates form.
 * These are used for dynamic field rendering and translations.
 */
const LABELS = {
  title: "Subscrição",
  initialInvestment: "Subscrição inicial",
  subscriptionType: "Periodicidade de novas subscrições",
  subscriptionMonthly: "Subscrições mensais",
  subscriptionQuarterly: "Subscrições trimestrais",
  subscriptionAnnual: "Subscrições anuais",
  monthlyValue: "Subscrições mensais",
  annualValue: "Subscrições anuais",
  termYears: "Prazo",
  interestRate: "Taxa de juro",
  interestTaxation: "Tributação sobre os juros",
  labelYears: "Anos",
  euriborRate: "Com base na euribor a 3 meses",
  retentionSource: "Retenção na fonte",
};

/**
 * TOOLTIPS
 *
 * Field-specific tooltips (translated) to assist the user with context.
 * Each tooltip includes a title, description and preferred placement.
 */
const TOOLTIPS: Record<string, Tooltip> = {
  initialInvestment: {
    title: __("Subscrição inicial", "df-simulators"),
    content: __(
      "O valor mínimo de subscrição inicial é de 100 euros e o máximo de 250 mil euros.",
      "df-simulators",
    ),
    placement: "right",
  },
  monthlySubscription: {
    title: __("Subscrições mensais", "df-simulators"),
    content: __("O valor mínimo nos reforços é de 10 euros.", "df-simulators"),
    placement: "right",
  },
  quarterlySubscription: {
    title: __("Subscrições trimestrais", "df-simulators"),
    content: __("O valor mínimo nos reforços é de 10 euros.", "df-simulators"),
    placement: "right",
  },
  annualSubscription: {
    title: __("Subscrições anuais", "df-simulators"),
    content: __("O valor mínimo nos reforços é de 10 euros.", "df-simulators"),
    placement: "right",
  },
  termYears: {
    title: __("Prazo de subscrição", "df-simulators"),
    content: __(
      "Cada subscrição pode ser mantida durante um máximo de 15 anos, mas só pode ser levantado a partir dos três meses.",
      "df-simulators",
    ),
    placement: "right",
  },
  interestRate: {
    title: __("Taxa de juro", "df-simulators"),
    content: __(
      "A taxa de juro base é determinada mensalmente no antepenúltimo dia útil do mês para vigorar durante o mês seguinte, através da média dos valores da Euribor a três meses observados nos últimos dez dias úteis antes do cálculo. Não pode ser inferior a 0% nem superior a 2,5%. A taxa de juro é calculada trimestralmente e, ao longo do período de subscrição acrescem os prémios de permanência (que vão dos 0,25% a partir do 2.º ano até aos 1,75% nos 14.º e 15.º meses). Os juros têm capitalização automática a cada 3 meses.",
      "df-simulators",
    ),
    placement: "right",
  },
  interestTaxation: {
    title: __("Retenção na fonte", "df-simulators"),
    content: __(
      "Taxa de retenção na fonte aplicada aos juros, variando conforme a região.",
      "df-simulators",
    ),
    placement: "right",
  },
};

/**
 * API endpoint and configuration for simulation submission.
 */
const API_BASE_URL = `${window.location.origin}/wp-json/simulators/v1/savings-certificates`;
const PROCESS_TYPE = 1;
const OPERATION_TYPE = 1;


/**
 * Simulator custom URL - Check if exists some custom URL for this block
 * [==START==]
 */
let MIDDLEWARE_API_URL = API_BASE_URL;
const rootElement = document.querySelector('[data-block="tool-block"]') as HTMLElement | null;
if ( rootElement !== null ) {
  const NEW_URL = rootElement.getAttribute('data-tool-managment');
  if ( NEW_URL !== null && NEW_URL !== '' ) {
    MIDDLEWARE_API_URL = NEW_URL;
  }
}
/* [==END==] */


/**
 * Form definition for the Savings Certificates simulator.
 *
 * Includes:
 * - Field configuration (labels, types, options)
 * - Field-level conditions
 * - Tooltips
 * - API integration details
 * - Yup validation schema reference
 */

export const forms: Forms = {
  savings_certificates: {
    validation: validations.savings_certificates,

    api: {
      url: MIDDLEWARE_API_URL,
      processType: PROCESS_TYPE,
      operationType: OPERATION_TYPE,
    },

    fields: [
      {
        type: "title",
        label: __("Dados da simulação", "df-simulators"),
      },
      {
        type: "heading",
        label: LABELS.title,
      },
      {
        type: "number",
        label: LABELS.initialInvestment,
        name: "initial_investment",
        icon: "currency",
        placeholder: "Mín. 100",
        tooltip: TOOLTIPS.initialInvestment,
      },
      {
        type: "heading",
        label: LABELS.subscriptionType,
      },
      {
        type: "radio",
        label: "",
        name: "additional_subscription_type",
        layout: "vertical",
        options: [
          { value: "quarterly", label: "Subscrições trimestrais" },
          /*{ value: "monthly", label: "Subscrições mensais" },*/
          { value: "annual", label: "Subscrições anuais" },
          { value: "none", label: "Sem subscrições adicionais" },
        ],
        defaultValue: "quarterly",
      },
      {
        type: "heading",
        label: LABELS.subscriptionMonthly,
        tooltip: TOOLTIPS.monthlySubscription,
        condition: [
          {
            field: "additional_subscription_type",
            value: "monthly",
          },
        ],
      },
      {
        type: "number",
        name: "monthly_subscription_amount",
        icon: "currency",
        placeholder: "Mín. 10",
        condition: [
          {
            field: "additional_subscription_type",
            value: "monthly",
          },
        ],
        clearWhenHidden: true,
      },
      {
        type: "heading",
        label: LABELS.subscriptionQuarterly,
        tooltip: TOOLTIPS.quarterlySubscription,
        condition: [
          {
            field: "additional_subscription_type",
            value: "quarterly",
          },
        ],
      },
      {
        type: "number",
        name: "quarterly_subscription_amount",
        icon: "currency",
        placeholder: "Mín. 10",
        condition: [
          {
            field: "additional_subscription_type",
            value: "quarterly",
          },
        ],
        clearWhenHidden: true,
      },
      {
        type: "heading",
        label: LABELS.subscriptionAnnual,
        tooltip: TOOLTIPS.annualSubscription,
        condition: [
          {
            field: "additional_subscription_type",
            value: "annual",
          },
        ],
      },
      {
        type: "number",
        name: "annual_subscription_amount",
        icon: "currency",
        placeholder: "Mín. 10",
        condition: [
          {
            field: "additional_subscription_type",
            value: "annual",
          },
        ],
        clearWhenHidden: true,
      },
      {
        type: "heading",
        label: LABELS.termYears,
        tooltip: TOOLTIPS.termYears
      },
      {
        type: "select",
        label: LABELS.labelYears,
        name: "term_years",
        options: [
          { value: " ", label: "Selecione uma opção" },
          { value: "1", label: "1 ano" },
          { value: "2", label: "2 anos" },
          { value: "3", label: "3 anos" },
          { value: "4", label: "4 anos" },
          { value: "5", label: "5 anos" },
          { value: "6", label: "6 anos" },
          { value: "7", label: "7 anos" },
          { value: "8", label: "8 anos" },
          { value: "9", label: "9 anos" },
          { value: "10", label: "10 anos" },
          { value: "11", label: "11 anos" },
          { value: "12", label: "12 anos" },
          { value: "13", label: "13 anos" },
          { value: "14", label: "14 anos" },
          { value: "15", label: "15 anos" },
        ],
        defaultValue: " ",
      },
      {
        type: "heading",
        label: LABELS.interestRate,
      },
      {
        type: "number",
        label: LABELS.euriborRate,
        name: "interest_rate",
        icon: "percentage",
        //defaultValue: 2.5,
        placeholder: "Introduza um valor entre 0 e 2,5",
        tooltip: TOOLTIPS.interestRate,
        step: "0.001",
        max: "20",
      },
      {
        type: "heading",
        label: LABELS.interestTaxation,
      },
      {
        type: "select",
        label: LABELS.retentionSource,
        name: "interest_taxation",
        options: [
          {
            value: "portugal_continental",
            label: "28% - Portugal Continental",
          },
          {
            value: "azores",
            label: "19,6% - Região Autónoma dos Açores",
          },
          {
            value: "madeira",
            label: "19,6% - Região Autónoma da Madeira",
          },
        ],
        defaultValue: "portugal_continental",
        tooltip: TOOLTIPS.interestTaxation,
      },
    ],
  },
};
